import React, { FC, useEffect, useState } from 'react';
import { View, StyleSheet, Text, ScrollView } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import { scale, fullWidth } from '@components/frame';
import {
  isWeb,
  isPhone,
  machineStatusColorName,
  machineStatusName,
  convertTimeLetter,
  formatTimeEvent,
  machineStatus,
  subStatusName,
  localTime,
} from '../../../../../../../../helpers';

import AnimatedHeader from '@components/common/AnimatedHeader';
import Line from '@components/common/line';
import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';
import NcFileIcon from '@v2/pages/machines/modules/events/components/details/components/NCFileIcon';

import { getBoxShadowStyle } from '@styles/shadow';
import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import { machineEventDetails, getNCFileByName } from '@reducers/manufacture';
import moment from 'moment';
import { CombinedEventProps, RenderTimeProps } from '@v2/pages/machines/common/types/event';
import { useNavigation, useRoute } from '@react-navigation/native';
import { MachinesScreen } from '@v2/pages/machines/common/enums';
import { MACHINES_MAIN_SCREEN } from '@v2/pages/machines/navigation/config';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { ColorFunction } from '@styles/colors/types';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import TimeDisplay from '@components/common/TimeDisplay';

interface StartEndTimeProps {
  dateTime: string;
  C: ColorFunction;
}

export const StartEndTime: FC<StartEndTimeProps> = ({ dateTime, C }) => {
  const localDateTime = localTime(dateTime);
  const timeValue = localDateTime.format('HH:mm:ss');

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <Text style={[font['Time 3 normal'], { color: C('Label/Light/Primary') }]}>
        {localDateTime.format('DD MMM YYYY')} {i18n.t('at')}{' '}
      </Text>
      <TimeDisplay
        alreadyConverted
        withHMSFormat
        value={timeValue}
        C={C}
        fontForDigit="Time 3 normal"
        fontForSecondDigit="Time 4 normal"
        fontForDelimiter="Caption 1"
      />
    </View>
  );
};

const RenderTime: FC<RenderTimeProps> = ({ time, label, C }) => {
  return (
    <View>
      <Text
        numberOfLines={1}
        ellipsizeMode="tail"
        style={[
          {
            color: C('Label/Light/Primary'),
          },
          font.Body,
        ]}
      >
        {time}
      </Text>
      <Text
        numberOfLines={1}
        ellipsizeMode="tail"
        style={[
          {
            color: C('Label/Light/Quaternary'),
            marginTop: scale(2),
          },
          font['Caption 1'],
        ]}
      >
        {i18n.t(label)}
      </Text>
    </View>
  );
};

const CombinedEventComponent: FC<CombinedEventProps> = ({ C, getNCFileByName, machineEventDetails, selectedItem }: CombinedEventProps) => {
  const { IS_NARROW_WEB, detailsWidth } = useDeviceSize();

  const [NCFileInfo, setNCFileInfo] = useState({});
  const [events, setEvents] = useState([]);
  const route = useRoute();
  const combinedEvent = {
    sequenceNumberStartedOn: route.params?.combinedEventStart,
    sequenceNumberEndedOn: route.params?.combinedEventEnd,
  };

  const [combinedEventDetails, setCombinedEventDetails] = useState(combinedEvent);

  const time = combinedEventDetails?.durationInMilliseconds && convertTimeLetter(combinedEventDetails.durationInMilliseconds, true);

  const navigation = useNavigation();

  useEffect(() => {
    (async () => {
      const data = await machineEventDetails({ ...combinedEvent, machineId: selectedItem.machineId });

      const first = data[0];
      const last = data[data.length - 1];
      const event = {
        sequenceNumberStartedOn: first.sequenceNumber,
        sequenceNumberEndedOn: last.sequenceNumber,
        status: first.status,
        subStatus: first.subStatus,
        ncFileMain: first.ncFileMain,
        ncFileStatus: first.ncFileStatus,
        startedOn: first.startedOn,
        endedOn: last.endedOn,
        durationInMilliseconds: Math.round(moment.duration(moment(last.endedOn).diff(moment(first.startedOn))).asMilliseconds()),
      };

      setCombinedEventDetails(event);

      setEvents(
        data.map((i: any) => ({
          ...i,
          sequenceNumberEndedOn: i.sequenceNumber,
          sequenceNumberStartedOn: i.sequenceNumber,
          durationInMilliseconds: Math.round(moment.duration(moment(i.endedOn).diff(moment(i.startedOn))).asMilliseconds()),
          durationInSeconds: Math.round(moment.duration(moment(i.endedOn).diff(moment(i.startedOn))).asSeconds()),
        }))
      );

      const ncFile = await getNCFileByName(event.ncFileMain);
      setNCFileInfo(ncFile.searchResult[0] || {});
    })();
  }, []);

  const config = [
    { name: 'Machine', value: selectedItem.fullName, noArrow: true },
    { name: 'Start Time', ValueComponent: <StartEndTime dateTime={combinedEventDetails.startedOn} C={C} />, noArrow: true },
    { name: 'End Time', ValueComponent: <StartEndTime dateTime={combinedEventDetails.endedOn} C={C} />, noArrow: true },
  ];

  const scheduledTime = {
    [machineStatus.ACTIVE]: formatTimeEvent(convertTimeLetter(NCFileInfo.runTimePerPcsSecond), false),
    [machineStatus.SETUP]: formatTimeEvent(convertTimeLetter(NCFileInfo.setupTimeSecond), false),
    [machineStatus.CHANGE]: formatTimeEvent(convertTimeLetter(NCFileInfo.changeTimePerPcsSecond), false),
    [machineStatus.NOTPRODUCTIVE]: '',
  };

  const timeLine = [
    {
      time: time ? formatTimeEvent(time, false) : 0,
      label: 'Actual',
    },
    {
      time: scheduledTime[combinedEventDetails.status],
      label: 'Scheduled',
    },
    {
      time: ' ',
      label: 'Paid',
    },
  ];

  const width = isPhone ? '100%' : detailsWidth - 1;

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  return [
    <View
      style={{
        flex: 1,
        backgroundColor: C('Background/Light/Detail'),
        width,
        right: 0,
      }}
    >
      <AnimatedHeader
        {...{
          text: i18n.t('Combined Event'),
          C,
          animated: false,
          back: true,
          backgroundColor: 'Background/Light/Detail',
          borderBottomWidth: 0,
          onBackPress: () => navigation.goBack(),
          customMargin: 0,
          customPadding: 0,
        }}
      />
      {combinedEventDetails.status ? (
        <ScrollView contentContainerStyle={{ paddingBottom: scale(50) }}>
          <View>
            <View style={{ width: '100%', alignItems: 'center', marginTop: scale(21), paddingTop: scale(41) }}>
              <View
                style={[
                  isPhone && !IS_NARROW_WEB
                    ? {
                        width: '100%',
                        paddingHorizontal: scale(15),
                      }
                    : IS_NARROW_WEB
                    ? { width: fullWidth - 30 }
                    : { width: scale(450) },
                  isWeb && shadowStyles,
                  isWeb && { borderRadius: scale(SCREEN_BORDER_RADIUS) },
                ]}
              >
                <View
                  style={{
                    width: '100%',
                  }}
                >
                  <View
                    style={{
                      width: '100%',
                      borderTopLeftRadius: scale(10),
                      borderTopRightRadius: scale(10),
                      paddingLeft: scale(15),
                      backgroundColor: C('Background/Light/Field'),
                      paddingTop: scale(5),
                    }}
                  >
                    <View
                      style={{ borderBottomColor: C('Hairline / Light / No trans'), borderBottomWidth: StyleSheet.hairlineWidth, height: scale(68) }}
                    >
                      <View>
                        <Text
                          numberOfLines={1}
                          ellipsizeMode="tail"
                          style={[
                            {
                              color: C('Label/Light/Tertiary'),
                            },
                            font.Body,
                          ]}
                        >
                          {i18n.t('Machine Productivity Status')}
                        </Text>
                      </View>
                      <View style={{ flexDirection: 'row' }}>
                        <View
                          style={[
                            {
                              borderRadius: scale(SCREEN_BORDER_RADIUS),
                              backgroundColor: C(machineStatusColorName[combinedEventDetails.status]),
                              paddingHorizontal: scale(7),
                              paddingVertical: scale(1),
                            },
                          ]}
                        >
                          <Text
                            numberOfLines={1}
                            ellipsizeMode="tail"
                            style={[
                              {
                                color: C('Label/Light/AlwaysLight Primary'),
                              },
                              font['Button 1'],
                            ]}
                          >
                            {i18n.t(machineStatusName[combinedEventDetails.status])}
                          </Text>
                        </View>
                      </View>
                      <Text
                        numberOfLines={1}
                        ellipsizeMode="tail"
                        style={[
                          {
                            color: C('Label/Light/Quaternary'),
                            marginTop: scale(2),
                          },
                          font['Caption 1'],
                        ]}
                      >
                        {i18n.t('Automatically determined')}
                      </Text>
                    </View>
                  </View>
                  <View
                    style={{
                      width: '100%',
                      paddingLeft: scale(15),
                      backgroundColor: C('Background/Light/Field'),
                      paddingTop: scale(5),
                    }}
                  >
                    <View
                      style={{ borderBottomColor: C('Hairline / Light / No trans'), borderBottomWidth: StyleSheet.hairlineWidth, height: scale(66) }}
                    >
                      <View>
                        <Text
                          numberOfLines={1}
                          ellipsizeMode="tail"
                          style={[
                            {
                              color: C('Label/Light/Tertiary'),
                            },
                            font.Body,
                          ]}
                        >
                          {i18n.t('Duration')}
                        </Text>
                      </View>
                      <View style={{ flexDirection: 'row', paddingRight: scale(62), justifyContent: 'space-between' }}>
                        {timeLine.map((item) => (
                          <RenderTime key={item.label} {...{ ...item, C }} />
                        ))}
                      </View>
                    </View>
                  </View>
                  {config.map((item, index) => (
                    <Line
                      {...{
                        inverted: true,
                        bigSize: true,
                        item,
                        index,
                        array: config,
                        C,
                        roundTop: false,
                        roundBottom: index === config.length - 1,
                      }}
                      key={item.name}
                    />
                  ))}
                </View>
              </View>

              <View
                style={[
                  isPhone
                    ? {
                        width: '100%',
                        paddingHorizontal: scale(15),
                      }
                    : { width: scale(450) },
                ]}
              >
                <View style={{ marginTop: scale(26) }}>
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={[
                      {
                        color: C('Label/Light/Tertiary'),
                        paddingLeft: scale(15),
                      },
                      font.Footnote,
                    ]}
                  >
                    {i18n.t('SINGLE EVENTS')}
                  </Text>
                </View>
                <View
                  style={[
                    isWeb && shadowStyles,
                    {
                      borderRadius: scale(SCREEN_BORDER_RADIUS),
                      marginTop: scale(5),
                    },
                  ]}
                >
                  {events.map((item, index) => (
                    <TouchableElement
                      onPress={() => {
                        navigation.navigate(MACHINES_MAIN_SCREEN + MachinesScreen.EVENT_DETAILS, {
                          eventDetailsStart: item.sequenceNumberStartedOn,
                          eventDetailsEnd: item.sequenceNumberEndedOn,
                        });
                      }}
                    >
                      {({ boxStyle, pressHoverColor }) => (
                        <View
                          style={[
                            boxStyle,
                            {
                              width: '100%',
                              paddingLeft: scale(14),
                              backgroundColor: C(pressHoverColor('Background/Light/Field')),
                            },
                            index === 0 && {
                              borderTopLeftRadius: scale(10),
                              borderTopRightRadius: scale(10),
                            },
                            index === events.length - 1 && {
                              borderBottomLeftRadius: scale(10),
                              borderBottomRightRadius: scale(10),
                            },
                          ]}
                          key={`${item.sequenceNumber}-${index}`}
                        >
                          <View
                            style={[
                              index !== events.length - 1 && {
                                borderBottomColor: C('Hairline / Light / No trans'),
                                borderBottomWidth: StyleSheet.hairlineWidth,
                              },
                              {
                                paddingTop: scale(11),
                                paddingBottom: scale(12),
                              },
                            ]}
                          >
                            <View
                              style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Text
                                numberOfLines={1}
                                ellipsizeMode="tail"
                                style={[
                                  {
                                    color: C('Label/Light/Primary'),
                                  },
                                  font['Button 1'],
                                ]}
                              >
                                {i18n.t(subStatusName[item.subStatus])}
                              </Text>
                              <View style={{ flexDirection: 'row', alignItems: 'center', paddingTop: scale(2) }}>
                                <Text
                                  numberOfLines={1}
                                  ellipsizeMode="tail"
                                  style={[
                                    {
                                      color: C('Label/Light/Tertiary'),
                                      marginRight: scale(17),
                                    },
                                    font.Footnote,
                                  ]}
                                >
                                  {formatTimeEvent(convertTimeLetter(item.durationInSeconds, false))}
                                </Text>
                                <Text
                                  numberOfLines={1}
                                  ellipsizeMode="tail"
                                  style={[
                                    {
                                      color: C('Label/Light/Tertiary'),
                                      marginRight: scale(10),
                                    },
                                    font['Footnote'],
                                  ]}
                                >
                                  {/* {formatSinceTime(item.startedOn)} */}
                                </Text>
                                <View style={{ marginRight: scale(16) }}>
                                  <Svg icon="chevronRightLight" color="Label/Light/Quinary" width={scale(7)} height={scale(12)} />
                                </View>
                              </View>
                            </View>
                            <Text
                              numberOfLines={1}
                              ellipsizeMode="tail"
                              style={[
                                {
                                  color: C('Label/Light/Tertiary'),
                                  marginTop: scale(1),
                                },
                                font.Footnote,
                              ]}
                            >
                              {i18n.t('Manual Data Input')}
                            </Text>
                            {item.ncFileActive && (
                              <Text
                                numberOfLines={1}
                                ellipsizeMode="tail"
                                style={[
                                  {
                                    color: C('Label/Light/Tertiary'),
                                    marginTop: scale(3),
                                  },
                                  font['Caption 1'],
                                ]}
                              >
                                {i18n.t('Active')}: <NcFileIcon {...{ C, ncFileStatus: item.ncFileStatusActive }} />
                                {item.ncFileActive}
                              </Text>
                            )}
                            {item.ncFileSelected && (
                              <Text
                                numberOfLines={1}
                                ellipsizeMode="tail"
                                style={[
                                  {
                                    color: C('Label/Light/Tertiary'),
                                    marginTop: scale(3),
                                  },
                                  font['Caption 1'],
                                ]}
                              >
                                {i18n.t('Selected')}: <NcFileIcon {...{ C, ncFileStatus: item.ncFileStatusSelected }} />
                                {item.ncFileSelected}
                              </Text>
                            )}
                          </View>
                        </View>
                      )}
                    </TouchableElement>
                  ))}
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
      ) : (
        <View style={{ width: '100%', alignItems: 'center', marginTop: scale(21), paddingTop: scale(41) }}>
          <SmallLoading />
        </View>
      )}
    </View>,
  ];
};

export default connect(({ machines: { selectedItem } }) => ({ selectedItem }), { machineEventDetails, getNCFileByName })(
  ColorWrapper(CombinedEventComponent)
);
